import React, { useEffect, useState } from "react";
import TableHeader from "../../common/MagicPageHeader";
import Header from "../../common/MagicTableHeader";
import MagicTable from "../../common/MagicTable";
import DownloadToXlsxFile from "../../../helper/DownloadToXlsxFile";
import ImageComponent from "../../mui/Image";
import { useFetchOperationsHRReportsQuery } from "../../../store/apis/reportsApis";

interface TabCategory {
  id: number;
  name: string;
  code: string;
}

interface OperationsHRTableProps {
  selectedTab: TabCategory | null;
  magicPageHeaderBtn: {
    text: string;
    variant: string;
    startIcon: React.ReactNode;
    onClick: () => void;
    sx: Record<string, string>;
  }[];
  magicTableHeaderBtn: any[];
  params:any;
  isInvalidSearch: boolean;
  onPaginationChange: (pageNo: number) => void;
  onSearch: (searchTerm: string) => void;
}

const OperationsHRTable: React.FC<OperationsHRTableProps> = ({
  selectedTab,
  magicTableHeaderBtn,
  params,
  isInvalidSearch,
  onPaginationChange,
  onSearch,
}) => {
  const [downloadParams, setDownloadParams] = useState<any>(null);
  const [downloadTrigger, setDownloadTrigger] = useState(false);

  const columns = [
    {
      field: "region",
      headerName: "Region",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "funder",
      headerName: "Funder",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "centre",
      headerName: "Centre",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "centreBusinessType",
      headerName: "Centre business type",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "state",
      headerName: "State",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "district",
      headerName: "City",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "cityManager",
      headerName: "City manager",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "budgetedStaff",
      headerName: "Budgeted Staff",
      flex: 1,
      minWidth: 150,
      sortable: true,
      type: 'number',
    },
    {
      field: "staffBeginningQuarter",
      headerName: "Staff in the Beginning of the Quarter",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "staffEndQuarter",
      headerName: "Staff Strength at the End of the Quarter",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "staffToBeHired",
      headerName: "Staff to be Hired at the End of the Quarter",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "onboardedStaff",
      headerName: "Onboarded Staff in This Quarter",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "empResignedQuarter",
      headerName: "No. of EMP Resigned in Qtr",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "infantAttrition",
      headerName: "Infant Attrition",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
  ];

  const {
    data: fetchedReports = {
      reports: [], 
      totalReports: 0, 
    },isLoading
  } = useFetchOperationsHRReportsQuery(params);

  const { data: downloadData,isLoading: downloadLoading } = useFetchOperationsHRReportsQuery(downloadParams, {
    skip: !downloadParams,
  });

  const handleDownload = () => {
    const downloadPayload = {
      ...params,
      pageNo: "1",
      pageSize: fetchedReports?.totalReports || "10",
    };
    setDownloadParams(downloadPayload);
    setDownloadTrigger(true); 
  };

  useEffect(() => {
    if (downloadData && downloadTrigger) {
      const headers = [
        "Region",
        "Funder",
        "Centre",
        "Centre Business Type",
        "State",
        "City",
        "City Manager",
        "Status",
        "Budgeted Staff",
        "Staff in Beginning of Quarter",
        "Staff Strength at End of Quarter",
        "Staff to be Hired at End of Quarter",
        "Onboarded Staff in Quarter",
        "Number of Employees Resigned in Quarter",
        "Infant Attrition",
      ];

      const rows =
        downloadData?.reports.map((entry: any) => [
          entry.region || "-",
          entry.funder || "-",
          entry.centre || "-",
          entry.centreBusinessType || "-",
          entry.state || "-",
          entry.district || "-",
          entry.cityManager || "-",
          entry.status || "-",
          entry.budgetedStaff || "-",
          entry.staffBeginningQuarter || "-",
          entry.staffEndQuarter || "-",
          entry.staffToBeHired || "-",
          entry.onboardedStaff || "-",
          entry.empResignedQuarter || "-",
          entry.infantAttrition || "-",
        ]) || [];

      const sheetData = [headers, ...rows];

      DownloadToXlsxFile({
        fileNamePrifix: "Operations-HR-Report",
        sheetData,
      });
      setDownloadTrigger(false);  
    }
    }, [downloadData, downloadTrigger]);  

  const magicPageHeaderBtn = [
    {
      text: "Download",
      variant: "outlined",
      startIcon: (
        <ImageComponent src="/assets/images/login/exportIcon.svg" alt="ExportIcon" />
      ),
      onClick: handleDownload, 
      loading: downloadLoading,
      sx: {
        background: "white",
        border: "1px solid #D0D5DD",
        borderRadius: "8px",
        color: "black",
      },
    },
  ];
  
  const totalPages = Math.max(1, Math.ceil((fetchedReports?.totalReports || 0) /10));
  return (
    <>
      <TableHeader
        title={`${selectedTab?.name || "Operations HR"} Report`}
        subTitle="Here you can view the report with all the details."
        buttons={magicPageHeaderBtn}
        chipLabel={`${fetchedReports?.totalReports || 0} entries`}
      />
      <Header
        buttons={magicTableHeaderBtn}
        onSearchfieldChange={(value) => onSearch(String(value))}
        isInvalidSearch={isInvalidSearch}
      />
      <MagicTable
        columns={columns}
        rows={fetchedReports?.reports}
        loading={isLoading}
        handlePageChange={(event, pageNo) => onPaginationChange(pageNo)}
        currentPage={parseInt(params?.pageNo) || 1}
        count={totalPages}
        rowCellsSx={{ paddingLeft: "16px", textAlign: "start" }}
        headerSx={{
          paddingLeft: "16px",
          textAlign: "start",
          fontWeight: 600,
          color: "#242424",
          padding: "13px 16px",
        }}
        tableSx={{ minHeight: "300px", width: "100%" }}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#DADADA",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 550,
            fontSize: "14px",
          },
          "& .MuiDataGrid-row": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },}}
      />
    </>
  );
};

export default OperationsHRTable;